function compareCode(userCode, expectedCode, language, options = {}) {
    // Enhanced default options
    const defaultOptions = {
        ignoreWhitespace: true,
        ignoreSemicolons: true,
        ignoreCase: false,
        ignoreComments: true,
        ignoreQuoteStyle: true, // Ignore ' vs " differences
        ignoreImportOrder: true, // Ignore ordering of import statements
        ignorePropOrder: true, // Ignore order of object properties/JSX props
        ignoreClassOrder: true, // Ignore order of CSS class names
        ignoreSQLCase: true, // Ignore case in SQL keywords
        language: language // 'generic', 'python', 'javascript', 'jsx', 'css', 'html'
    };

    const settings = { ...defaultOptions, ...options };

    // Enhanced normalization function
    function normalizeCode(code) {
        let normalized = code;

        // Remove comments based on language
        if (settings.ignoreComments) {
            switch (settings.language.toLowerCase()) {
                case 'python':
                    normalized = normalized.replace(/#.*$/gm, '');
                    normalized = normalized.replace(/'''[\s\S]*?'''/g, '');
                    normalized = normalized.replace(/"""[\s\S]*?"""/g, '');
                    break;
                case 'css':
                case 'scss':
                    normalized = normalized.replace(/\/\*[\s\S]*?\*\//g, '');
                    break;
                case 'sh':
                    normalized = normalized.replace(/#.*$/gm, '');
                    break;
                case 'sql':
                    normalized = normalized.replace(/--.*$/gm, '');
                    normalized = normalized.replace(/\/\*[\s\S]*?\*\//g, '');
                    break;
                case 'jsx':
                case 'javascript':
                case 'html':
                case 'generic':
                default:
                    normalized = normalized.replace(/\/\*[\s\S]*?\*\//g, '');
                    normalized = normalized.replace(/\/\/.*$/gm, '');
                    break;
            }
        }

        // Language-specific normalizations
        switch (settings.language.toLowerCase()) {
            case 'jsx':
                normalized = normalizeJSX(normalized);
                break;
            case 'css':
                normalized = normalizeCSS(normalized);
                break;
            case 'html':
                normalized = normalizeHTML(normalized);
                break;
            case 'sql':
                normalized = normalizeSQL(normalized);
                break;
            case 'sh':
                normalized = normalizeShell(normalized);
                break;
            default:
                break
        }

        // Common normalizations
        if (settings.ignoreWhitespace) {
            normalized = normalized.replace(/\s+/g, '');
        }

        if (settings.ignoreSemicolons) {
            normalized = normalized.replace(/;/g, '');
        }

        if (settings.ignoreQuoteStyle) {
            // Replace all quotes with double quotes, accounting for escaping
            normalized = normalized.replace(/'([^'\\]|\\.)*'/g, match =>
                `"${match.slice(1, -1)}"`
            );
        }

        if (settings.ignoreCase) {
            normalized = normalized.toLowerCase();
        }

        return normalized;
    }

    // JSX-specific normalization
    function normalizeJSX(code) {
        let normalized = code;

        if (settings.ignorePropOrder) {
            // Extract JSX elements and normalize prop order
            normalized = normalized.replace(
                /<[^>]+>/g,
                tag => {
                    if (tag.match(/^<[^/]/)) { // Opening tags only
                        const [tagStart, ...props] = tag.split(' ');
                        if (props.length) {
                            const sortedProps = props
                                .join(' ')
                                .split(/(?<==["'].*?)(?=\w+=['"])/g)
                                .filter(Boolean)
                                .sort()
                                .join(' ');
                            return `${tagStart} ${sortedProps}`;
                        }
                    }
                    return tag;
                }
            );
        }

        if (settings.ignoreImportOrder) {
            // Normalize import statements order
            const importRegex = /import\s+.*?from\s+['"].*?['"]/g;
            const imports = normalized.match(importRegex) || [];
            const sortedImports = imports.sort().join(';');
            normalized = normalized.replace(importRegex, '');
            normalized = sortedImports + normalized;
        }

        return normalized;
    }

    // CSS-specific normalization
    function normalizeCSS(code) {
        let normalized = code;

        if (settings.ignoreClassOrder) {
            // Normalize class names order in selectors
            normalized = normalized.replace(/\.([\w-\s]+)/g, match => {
                const classes = match.slice(1).split('.').filter(Boolean);
                return '.' + classes.sort().join('.');
            });
        }

        // Normalize property order within rules
        normalized = normalized.replace(
            /{([^}]+)}/g,
            match => {
                const properties = match.slice(1, -1)
                    .split(';')
                    .filter(Boolean)
                    .map(prop => prop.trim())
                    .sort();
                return '{' + properties.join(';') + '}';
            }
        );

        // Normalize color values (e.g., #fff to #ffffff)
        normalized = normalized.replace(/#([0-9a-f]{3})([^0-9a-f]|$)/gi,
            (_, group) => '#' + group[0].repeat(2) + group[1].repeat(2) + group[2].repeat(2)
        );

        return normalized;
    }

    // HTML-specific normalization
    function normalizeHTML(code) {
        let normalized = code;

        // Normalize boolean attributes
        normalized = normalized.replace(
            /(\s)(checked|selected|disabled|readonly|required|hidden)="[^"]*"/g,
            '$1$2'
        );

        // Normalize self-closing tags
        normalized = normalized.replace(
            /<(area|base|br|col|embed|hr|img|input|link|meta|param|source|track|wbr)([^>]*)>/g,
            '<$1$2/>'
        );

        return normalized;
    }

    // SQL-specific normalization
    function normalizeSQL(code) {
        let normalized = code;

        if (settings.ignoreSQLCase) {
            normalized = normalized.toLowerCase();
        }

        normalized = normalized.replace(/\s+/g, ' ').trim();

        return normalized;
    }

    function normalizeShell(code) {
        let normalized = code;
        // Remove comments (except shebang line)
        normalized = normalized.replace(/(^#!.*$)|(^#.*$)/gm, (match, p1) => p1 || "");
        // Normalize whitespace
        normalized = normalized.replace(/\s+/g, " ").trim();
        // Normalize command substitution
        normalized = normalized.replace(/`([^`]*)`/g, "$(\\1)");
        // Normalize quoting styles
        normalized = normalized.replace(/'([^']*)'/g, '"$1"');
        // Normalize case for commands
        normalized = normalized.replace(/\b[A-Z]+\b/g, match => match.toLowerCase());

        return normalized;
    }


    // Enhanced difference finding
    function findDifferences(str1, str2) {
        let differences = [];
        const maxLength = Math.max(str1.length, str2.length);
        let diffStart = -1;
        let currentDiff = '';

        for (let i = 0; i < maxLength; i++) {
            if (str1[i] !== str2[i]) {
                if (diffStart === -1) diffStart = i;
                currentDiff += str1[i] || '';
            } else if (diffStart !== -1) {
                differences.push({
                    position: diffStart,
                    expected: str2.slice(diffStart, i) || '<missing>',
                    received: currentDiff || '<missing>',
                    context: str2.slice(Math.max(0, diffStart - 10), Math.min(str2.length, i + 10))
                });
                diffStart = -1;
                currentDiff = '';

                if (differences.length >= 5) break;
            }
        }

        // Handle difference at end of string
        if (diffStart !== -1) {
            differences.push({
                position: diffStart,
                expected: str2.slice(diffStart) || '<missing>',
                received: currentDiff || '<missing>',
                context: str2.slice(Math.max(0, diffStart - 10))
            });
        }

        return differences;
    }

    // Normalize and compare
    const normalizedUser = normalizeCode(userCode);
    const normalizedExpected = normalizeCode(expectedCode);
    const isMatch = normalizedUser === normalizedExpected;

    return {
        isMatch,
        normalizedUser,
        normalizedExpected,
        differences: !isMatch ? findDifferences(normalizedUser, normalizedExpected) : null
    };
}

function areAllKeysPresent(userInput, expectedInput) {
    if (Object.keys(userInput).length) {
        return Object.keys(expectedInput).every(key => key in userInput);
    } else {
        return false;
    }
}

function validateFiles(userInput, expectedInput) {

    // Handle case where both userInput and expectedInput are empty objects
    if (Object.keys(userInput).length === 0 && Object.keys(expectedInput).length === 0) {
        return true;
    }

    const allFilesPresent = areAllKeysPresent(userInput, expectedInput);
    if (allFilesPresent) {
        return Object.keys(expectedInput).every(file => {
            const ext = file.split('.').pop()
            const { isMatch } = compareCode(userInput[file], expectedInput[file], ext);
            return isMatch;
        });
    }
    return false;
}

export { compareCode, validateFiles };
export default validateFiles;
